(() => {
  if (customElements.get('infinite-list')) return;
  const itemSelector = '[data-product-card], .ny-search-card, .article-card, .collection-tile';
  customElements.define('infinite-list', class extends HTMLElement {
    connectedCallback() {
      if (!window.IntersectionObserver || this.events) return;
      this.items = this.querySelector('[data-list-items]');
      this.controls = this.querySelector('[data-list-controls]');
      this.feedback = this.querySelector('[data-list-feedback]');
      if (!this.items || !this.controls || !this.feedback) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      const reconnecting = this.boundItems === this.items;
      this.boundItems = this.items;
      if (!reconnecting) this.initialURL = new URL(location.href);
      this.pageParam = this.controls.dataset.pageParam;
      this.lastPage = Number(this.controls.dataset.page);
      if (!reconnecting) {
        this.startPage = this.lastPage;
        this.pageURLs = new Map([[this.lastPage, this.initialURL.href]]);
      }
      this.keys = new Set();
      this.visible = new Set();
      this.version = 0;
      this.emptyPages = 0;
      this.paused = false;
      this.suspended = false;
      this.status = this.querySelector('[data-list-status]');
      this.more = this.querySelector('[data-list-more]');
      this.retry = this.querySelector('[data-list-retry]');
      this.footerButton = this.querySelector('[data-list-footer]');
      this.footer = document.querySelector('.site-footer');
      this.next = this.pageURL(this.controls.dataset.next);
      if (this.startPage > 1) {
        const previous = this.querySelector('[data-list-pagination] [rel="prev"]');
        if (previous) {
          const earlier = previous.cloneNode(true);
          earlier.className = 'list-pagination__earlier';
          earlier.textContent = this.feedback.dataset.earlier;
          earlier.href = this.pageURL(previous.getAttribute('href'))?.href || previous.href;
          this.items.before(earlier);
        }
      }
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => entry.isIntersecting ? this.visible.add(entry.target) : this.visible.delete(entry.target));
      });
      this.registerItems(this.items, this.lastPage, reconnecting);
      this.sentinel = new IntersectionObserver(() => this.check(), { rootMargin: '0px 0px 500px 0px' });
      this.sentinel.observe(this.controls);
      this.more.addEventListener('click', () => { this.paused = false; this.load(true); }, options);
      this.retry.addEventListener('click', () => this.load(true), options);
      this.footerButton.hidden = !this.footer;
      this.footerButton.addEventListener('click', () => this.openFooter(), options);
      this.addEventListener('click', event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]');
        const item = link?.closest(itemSelector);
        if (!item || !this.items.contains(item) || link.target === '_blank' || link.hasAttribute('download')) return;
        this.savePosition(item);
        this.suspended = true;
        this.cancel();
      }, options);
      window.addEventListener('pagehide', () => {
        if (!this.suspended) this.savePosition();
        this.suspended = true;
        this.cancel();
        this.releaseScrollRestoration();
      }, options);
      window.addEventListener('pageshow', event => {
        if (event.persisted) { this.suspended = false; this.paint(); this.check(); }
      }, options);
      document.addEventListener('visibilitychange', () => { if (!document.hidden) this.check(); }, options);
      document.addEventListener('close', () => requestAnimationFrame(() => this.check()), { ...options, capture: true });
      document.addEventListener('submit', event => { if (!event.defaultPrevented) { this.suspended = true; this.cancel(); } }, options);
      document.addEventListener('shopify:section:unload', event => { if (event.target.contains(this)) this.disconnectedCallback(); }, options);
      this.setAttribute('data-list-ready', '');
      this.feedback.hidden = false;
      this.paint();
      if (this.controls.dataset.next && !this.next) this.paint('error');
      this.restorePosition();
    }

    pageURL(value) {
      if (!value) return null;
      try {
        const url = new URL(value, this.initialURL);
        if (url.origin !== this.initialURL.origin || url.pathname !== this.initialURL.pathname) return null;
        url.searchParams.delete('section_id');
        url.searchParams.delete('sections');
        url.hash = '';
        for (const key of new Set(this.initialURL.searchParams.keys())) {
          if (key === this.pageParam || key === 'section_id' || key === 'sections') continue;
          if (!url.searchParams.has(key)) this.initialURL.searchParams.getAll(key).forEach(value => url.searchParams.append(key, value));
          if (JSON.stringify(url.searchParams.getAll(key)) !== JSON.stringify(this.initialURL.searchParams.getAll(key))) return null;
        }
        return url;
      } catch { return null; }
    }

    itemKey(item) {
      if (item.dataset.productId) return 'product:' + item.dataset.productId;
      const link = item.matches('a') ? item : item.querySelector('h2 a, h3 a, a[href]');
      if (!link) return '';
      try { return new URL(link.getAttribute('href'), this.initialURL).pathname; } catch { return ''; }
    }

    registerItems(root, page, preservePage = false) {
      root.querySelectorAll(itemSelector).forEach(item => {
        if (!preservePage || !item.dataset.listPage) item.dataset.listPage = String(page);
        item.dataset.listKey = this.itemKey(item);
        if (item.dataset.listKey) this.keys.add(item.dataset.listKey);
        this.observer.observe(item);
      });
    }

    paint(state) {
      this.toggleAttribute('data-list-paused', this.paused);
      this.toggleAttribute('data-list-error', state === 'error');
      this.status.textContent = this.feedback.dataset[state || (this.paused ? 'paused' : this.next ? 'idle' : 'end')];
      this.more.textContent = this.feedback.dataset[this.paused ? 'resume' : 'more'];
      this.more.hidden = !this.next || state === 'error';
      this.more.disabled = Boolean(this.request);
      this.retry.hidden = state !== 'error';
      this.retry.disabled = Boolean(this.request);
      this.footerButton.hidden = !this.footer || !this.next;
    }

    check() {
      if (this.paused || this.suspended || this.restoring || document.hidden || !this.isConnected || this.hasAttribute('data-list-error')) return;
      if (document.querySelector('dialog[open]') || this.footer?.contains(document.activeElement)) return;
      const box = this.controls.getBoundingClientRect();
      if (box.top < window.innerHeight + 500 && box.bottom > 0) this.load();
    }

    async load(manual = false) {
      if (this.request || !this.next || !this.isConnected || this.suspended) return;
      const url = this.next;
      const page = Number(url.searchParams.get(this.pageParam));
      if (page !== this.lastPage + 1) { this.paint('error'); return; }
      const version = ++this.version;
      const controller = new AbortController();
      this.request = controller;
      const action = manual && [this.retry, this.more].includes(document.activeElement) ? document.activeElement : null;
      let focusMoved = false;
      const onFocus = event => { if (event.target !== action) focusMoved = true; };
      if (action) document.addEventListener('focusin', onFocus);
      const timer = setTimeout(() => controller.abort(), 15000);
      this.setAttribute('aria-busy', 'true');
      this.paint('loading');
      try {
        const endpoint = new URL(url);
        endpoint.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(endpoint, { credentials: 'same-origin', headers: { Accept: 'text/html' }, signal: controller.signal });
        if (!response.ok) throw new Error('List request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        if (version !== this.version || !this.isConnected || this.suspended) return;
        const incoming = doc.getElementById(this.id);
        const controls = incoming?.querySelector('[data-list-controls]');
        const items = incoming?.querySelector('[data-list-items]');
        if (!controls || !items || Number(controls.dataset.page) !== page || controls.dataset.pageParam !== this.pageParam) throw new Error('Invalid list page');
        const next = this.pageURL(controls.dataset.next);
        if (controls.dataset.next && (!next || Number(next.searchParams.get(this.pageParam)) !== page + 1)) throw new Error('Invalid next page');
        const seen = new Set(this.keys);
        items.querySelectorAll(itemSelector).forEach(item => {
          const key = this.itemKey(item);
          if (key && seen.has(key)) item.remove();
          else if (key) seen.add(key);
        });
        const newItems = [...items.querySelectorAll(itemSelector)];
        this.emptyPages = newItems.length ? 0 : this.emptyPages + 1;
        if (this.emptyPages > 2 && next) throw new Error('No additional items');
        items.querySelectorAll('img').forEach(img => { img.loading = 'lazy'; img.setAttribute('fetchpriority', 'auto'); });
        items.querySelectorAll('script:not([type="application/json"])').forEach(script => script.remove());
        this.registerItems(items, page);
        this.items.append(...items.children);
        this.lastPage = page;
        this.pageURLs.set(page, url.href);
        this.next = next;
        this.controls.dataset.page = String(page);
        this.controls.dataset.next = next?.href || '';
        const oldNav = this.querySelector('[data-list-pagination]');
        const newNav = controls.querySelector('[data-list-pagination]');
        if (oldNav && newNav) oldNav.replaceWith(newNav);
        else if (newNav) this.controls.append(newNav);
        else oldNav?.remove();
        this.dispatchEvent(new CustomEvent('northyard:list:append', { bubbles: true }));
        if (this.items.querySelector('[data-product-card]')) window.Northyard?.initProductCards?.(this.items);
        this.paint(this.next ? 'loaded' : 'end');
        if (action && !focusMoved && !document.querySelector('dialog[open]')) {
          const target = newItems[0]?.querySelector('h2 a, h3 a, [data-card-product-link]') || newItems[0] || this.status;
          if (!target.matches('a, button, input')) target.tabIndex = -1;
          target.focus({ preventScroll: true });
        }
      } catch {
        if (version === this.version && this.isConnected && !this.suspended && !this.paused) {
          this.paint('error');
          if (action && !focusMoved && !document.querySelector('dialog[open]')) {
            this.retry.disabled = false;
            this.retry.focus({ preventScroll: true });
          }
        }
      } finally {
        document.removeEventListener('focusin', onFocus);
        clearTimeout(timer);
        if (version === this.version) {
          this.request = null;
          this.removeAttribute('aria-busy');
          this.more.disabled = false;
          this.retry.disabled = false;
          if (!this.hasAttribute('data-list-error')) requestAnimationFrame(() => this.check());
        }
      }
    }

    cancel() {
      ++this.version;
      this.request?.abort();
      this.request = null;
      this.removeAttribute('aria-busy');
    }

    openFooter() {
      if (!this.footer) return;
      this.paused = true;
      this.cancel();
      this.paint();
      this.footer.tabIndex = -1;
      this.footer.focus({ preventScroll: true });
      this.footer.scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }

    savePosition(item) {
      if (!this.isConnected || this.suspended) return;
      const visible = [...this.visible].filter(node => node.isConnected && node.getBoundingClientRect().bottom > 0);
      item ||= visible.sort((a, b) => Math.abs(a.getBoundingClientRect().top) - Math.abs(b.getBoundingClientRect().top))[0];
      if (!item?.dataset.listKey) return;
      const page = Number(item.dataset.listPage);
      const url = this.pageURLs.get(page);
      if (!url) return;
      const state = { ...(history.state || {}), nyInfiniteList: { id: this.id, page, key: item.dataset.listKey, offset: item.getBoundingClientRect().top } };
      try { history.replaceState(state, '', url); } catch {}
    }

    restorePosition() {
      const saved = history.state?.nyInfiniteList;
      if (!saved || saved.id !== this.id || saved.page !== this.startPage || !['back_forward', 'reload'].includes(performance.getEntriesByType('navigation')[0]?.type)) return;
      const item = [...this.items.querySelectorAll(itemSelector)].find(item => item.dataset.listKey === saved.key);
      if (!item) return;
      if (this.previousScrollRestoration === undefined) {
        this.previousScrollRestoration = history.scrollRestoration;
        history.scrollRestoration = 'manual';
      }
      this.restoring = true;
      let interrupted = false;
      for (const type of ['pointerdown', 'wheel', 'touchstart', 'keydown']) window.addEventListener(type, () => { interrupted = true; }, { once: true, passive: true, signal: this.events.signal });
      const restore = () => {
        if (!interrupted && this.isConnected && Number.isFinite(saved.offset)) window.scrollBy({ top: item.getBoundingClientRect().top - saved.offset, behavior: 'instant' });
      };
      window.addEventListener('pageshow', restore, { once: true, signal: this.events.signal });
      requestAnimationFrame(() => requestAnimationFrame(restore));
      Promise.race([document.fonts.ready, new Promise(resolve => setTimeout(resolve, 800))]).then(() => {
        requestAnimationFrame(() => { restore(); this.restoring = false; this.check(); });
      });
    }

    releaseScrollRestoration() {
      if (this.previousScrollRestoration === undefined) return;
      if (history.scrollRestoration === 'manual') history.scrollRestoration = this.previousScrollRestoration;
      this.previousScrollRestoration = undefined;
    }

    disconnectedCallback() {
      this.releaseScrollRestoration();
      this.cancel();
      this.events?.abort();
      this.events = null;
      this.observer?.disconnect();
      this.sentinel?.disconnect();
      this.removeAttribute('data-list-ready');
      this.removeAttribute('data-list-error');
      this.querySelector('.list-pagination__earlier')?.remove();
      if (this.feedback) this.feedback.hidden = true;
    }
  });
})();
